import Quill from 'quill';

const Parchment = Quill.import('parchment');
const ListItem : any = Quill.import('formats/list');
const Module = Quill.import('core/module');

class CustomIndentAttributor extends Parchment.StyleAttributor {

    static KEY_NAME: string = 'padding-left';
    static WHITELIST: string[] = ['1', '2', '3', '4', '5', '6', '7', '8'];
    static PIXEL_LENGTH: number = 40;

    constructor() {                
        super('indent', CustomIndentAttributor.KEY_NAME, {
            scope: Parchment.Scope.BLOCK,
            whitelist: CustomIndentAttributor.WHITELIST
        });
    }

    pixelsToIndent(pixels: string): number {
        let indentValue = 0;
        const match = pixels.match(new RegExp('(.*)px'));
        if (match) {
            const pixelValue = Number(match[1]);
            indentValue = (pixelValue / CustomIndentAttributor.PIXEL_LENGTH);
        }
        return indentValue;
    }

    indentToPixels(indent: number): string {
        return (indent * CustomIndentAttributor.PIXEL_LENGTH) + 'px';
    }

    canAdd(node: any, pixelValue: string) {
        const indentValue = `${this.pixelsToIndent(pixelValue)}`;
        return CustomIndentAttributor.WHITELIST.includes(indentValue);
    }

    add(node: any, indent: number) {
        if (indent <= 0) {
            super.remove(node);
            return true
        }
        const pixelValue = this.indentToPixels(indent);
        return super.add(node, pixelValue);
    }

    value(node: any) {
        const pixelValue: string = node.style[CustomIndentAttributor.KEY_NAME];
        return this.pixelsToIndent(pixelValue);
    }
}

class OwnListItem extends ListItem {
    private domNode;

    constructor(scroll, domNode) { 
        super(scroll, domNode);
        this.domNode = domNode;
        
        setTimeout(() => {
            this.setListStyles(this.domNode);
            this.setSizeToIdentifier();
        }, 0);
    }

    async format(name, value) {
        await super.format(name, value);
        if(value) {
            this.setSizeToIdentifier()
        }
    }

    async formatAt(index, length, name, value) {
          await super.formatAt(index, length, name, value);
          this.setSizeToIdentifier()
    }

    private setListStyles(domNode) {
        domNode.style.wordBreak = 'break-all';
        let styleElem = document.head.appendChild(document.createElement('style'));
        styleElem.innerHTML = `.ql-editor ol li { padding-left: 0;} 
        .ql-editor ol li > .ql-ui:before {margin-left: 0 !important;text-align: left;width: auto;}`;
    }

    private getIdentifierNode(domNode) {
        return domNode.getElementsByClassName('ql-ui')[0];
    }

    private getAmountOfTextNodes(domNode) {
        return domNode.children.length;
    }

    private getChildrenFontSize(domNode) {
        return domNode.children[1]?.style.getPropertyValue('font-size');
    }

    private containsDirectText(domNode) {
        return [...domNode.childNodes]
            .some(n => n.nodeType === Node.TEXT_NODE
                  && n.nodeValue.trim() !== '');
    }

    private setSizeToIdentifier() {
        const identifierNode = this.getIdentifierNode(this.domNode);
        identifierNode.style.position = 'relative';

        if(this.getAmountOfTextNodes(this.domNode) == 2 && !this.containsDirectText(this.domNode)) {
            let lineFontSize = this.getChildrenFontSize(this.domNode);
            this.domNode.style.fontSize = lineFontSize;
        }
    }
}

class PreserveFormatOnEnter extends Module {
    private keys = new Set(['Enter']);
    private toolbarControls: Map<string, HTMLElement> = new Map();
    private excludeControls =  new Set(['indent', 'list', 'header', 'align', 'script']);

    constructor(quill, options) {
        super(quill, options);
        this.quill.root.addEventListener('keyup', (event) => {
            if (this.keys.has(event.key))
                this.preserveFormat(this.quill.getSelection());
        });
        this.initToolbarControls();
    }

    /**
     * Initializes the toolbar controls map
     */
    private initToolbarControls() {
        const toolbar : any = this.quill.getModule('toolbar');
        if (toolbar)
            toolbar?.controls?.forEach(control =>  this.toolbarControls.set(control[0], control[1]));
    }

    /**
     * Checks if the the line in cursor position is empty to find the previus line and apply the same format
     * @param range
     * @returns
     */
    preserveFormat(range) {
        let index = range.index
        const [line, offset] = this.quill.getLine(index);

        if (index === 0 || !line || line.domNode.outerText !==  '\n')
            return true;

        let lastLine = '\n';
        while(index-- > 0 && lastLine ===  '\n'){
            lastLine = this.quill.getLine(index)[0].domNode.outerText;
        }

        const formats = this.quill.getFormat(index);
        this.excludeControls.forEach(exclude => delete formats[exclude]);  //Exclude controls
        setTimeout(() => {
                this.quill.formatLine(range.index, 1, formats);
                this.updateToolbar(formats);
            }, 0);
        return true;
    }
    /**
     * Selects/checks the toolbar buttons according to the format
     * @param formats
     */
    updateToolbar(formats) {
        Object.keys(formats).forEach(format => {
            const control = this.toolbarControls.get(format);
            if (control.tagName === 'BUTTON') {
                control.click();
            } else if (control.tagName === 'SELECT') {
                (control as any).value = formats[format];
                control.dispatchEvent(new Event('change'))
            }
        });
    }
}

export function setQuillInlineStyle(): void {
    // Font
    const FontStyle : any = Quill.import('attributors/style/font');
    delete FontStyle.whitelist                                  //Fix bug not loading font styles.  Ref: https://github.com/slab/quill/issues/1580
    Quill.register(FontStyle, true);

    // Size
    const SizeStyle : any = Quill.import('attributors/style/size');
    delete SizeStyle.whitelist
    Quill.register(SizeStyle, true);

    // Align
    const AlignStyle : any = Quill.import('attributors/style/align');
    delete AlignStyle.whitelist;
    Quill.register(AlignStyle, true);

    // Direction
    const DirectionStyle : any = Quill.import('attributors/style/direction');
    delete DirectionStyle.whitelist;
    Quill.register(DirectionStyle, true);

    // Color
    const ColorStyle : any = Quill.import('attributors/style/color');
    delete ColorStyle.whitelist
    Quill.register(ColorStyle, true);

    // Background
    const BackgroundStyle : any  = Quill.import('attributors/style/background');
    delete BackgroundStyle.whitelist
    Quill.register(BackgroundStyle, true);

    // Indent
    Quill.register('formats/indent', new CustomIndentAttributor());

    // ListItem
    Quill.register(OwnListItem, true);

    // Preserve format
    Quill.register('modules/preserveFormatOnEnter', PreserveFormatOnEnter);

}
